import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import styled from 'styled-components';
import { panel } from './panelTheme';
import { PropertiesPanelBullet, PropertiesPanelBulletsProvider, PropertiesPanelPropertyContainer } from './propertiesPanelProperty';

// LA RIGA DI UN CANALE — il nome, il valore che si tocca, e la casella della sua mappa.
//
// NON È UN ACCORDION, ed è una differenza di sostanza: un accordion è un TITOLO che introduce un
// gruppo, questo è un VALORE — un colore, una ruvidità — che si può anche dipingere con una
// texture. Il valore resta sulla riga, sempre visibile; la mappa e le impostazioni che la
// riguardano stanno dentro, a richiesta. Sono due domande diverse sullo stesso parametro, e per
// questo non si contendono la riga.
//
// Prima ogni canale era una card che si apriva, e per leggere il colore di base bisognava aprirla:
// sette gruppi chiusi in fila dicevano soltanto i propri nomi. Chi lavora su un materiale il
// colore lo vuole vedere, non trovare.
//
// QUATTRO COLONNE, UNA SOLA ELASTICA — segnaposto · nome · valore · mappa. L'incolonnamento non è
// una proprietà della riga, è una proprietà del PANNELLO: con tre colonne fisse, due canali che
// stanno in card diverse hanno il valore e l'anteprima alla stessa x, senza che nessuno debba
// misurare niente. Chi non ha una mappa lascia la colonna vuota — è il prezzo dichiarato
// dell'incolonnamento, e senza pagarlo le anteprime ballerebbero riga per riga.
//
// DUE COMANDI, UNO STATO. Il nome e l'anteprima aprono la stessa cosa, quindi lo stato sta sulla
// RIGA e non su uno dei due: con `aria-expanded` come portatore ci sarebbero due verità da tenere
// d'accordo. L'attributo c'è su entrambi, ma lo scrive un posto solo.

/** L'altezza di una riga è quella dell'anteprima: così le righe senza mappa non si accorciano. */
const MAP_SIZE = '32px';

// IL NOME SI PRENDE LA COLONNA DEL VALORE QUANDO IL VALORE NON C'È, com'è nel mockup. Un canale come
// «Occlusion / Roughness / Metallic» non ha un valore da mostrare — è un contenitore di mappe — e
// troncarne il nome accanto a una colonna vuota buttava via lo spazio che serviva.
const Name = styled.button<{ $full?: boolean }>`
    grid-column: ${(props) => (props.$full ? '2 / 4' : 'auto')};
    min-width: 0;
    padding: 0;
    border: 0;
    background: transparent;
    font-family: inherit;
    font-size: 12px;
    font-weight: 500;
    text-align: left;
    color: ${panel.ink};
    cursor: pointer;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &:focus-visible {
        outline: 2px solid ${panel.accent};
        outline-offset: 2px;
        border-radius: 3px;
    }

    /* UN CANALE CHE NON HA NIENTE DA APRIRE NON È UN COMANDO: è soltanto il proprio nome, e allora
       lo si monta come uno span. Stessa riga e stesso peso — a cambiare è il cursore, perché uno
       che promette un clic senza darlo è peggio di nessun cursore. */
    &:not(button) {
        cursor: default;
    }
`;

const Head = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    min-width: 0;

    /* LA RIGA DEL PARAMETRO NON DISEGNA LA PROPRIA RIGA: la riga è questa. Il nome ce l'ha già il
       canale — ripeterlo accanto al controllo sarebbe la stessa parola due volte — e la griglia a
       due colonne del parametro spezzerebbe in due uno spazio che qui è già una colonna sola.
       Il contenuto passa e la scatola sparisce, senza che nessuno la ridisegni. */
    & > ${PropertiesPanelPropertyContainer} {
        display: contents;
    }

    & > ${PropertiesPanelPropertyContainer} > * {
        flex: 1 1 auto;
        min-width: 0;
    }

    & > ${PropertiesPanelPropertyContainer} > label {
        display: none;
    }

    /* QUI LO SLIDER CEDE PRIMA. Una riga di canale porta due colonne che una riga di parametro non
       ha — il segnaposto e l'anteprima — e il pavimento dei 44px, giusto altrove, spingerebbe il
       campo del valore fuori dalla colonna. Il campo non si stringe mai: è quello che si legge. */
    input[type='range'] {
        min-width: 32px;
    }
`;

const MapButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: ${MAP_SIZE};
    height: ${MAP_SIZE};
    padding: 0;
    border: 0;
    border-radius: 5px;
    background: transparent;
    cursor: pointer;

    &:focus-visible {
        outline: 2px solid ${panel.accent};
        outline-offset: 1px;
    }
`;

// L'ANTEPRIMA È LA MANIGLIA, non un chevron: piena dice «qui c'è una texture» prima ancora che si
// legga il nome, e vuota lascia vedere il posto dove la texture andrà.
//
// ESPORTATA PERCHÉ LO SLOT DELLA MAPPA, dentro il cassetto, mostra la stessa miniatura: sono la
// stessa immagine in due posti, e due disegni diversi direbbero che sono due texture.
//
// LA TEXTURE È UN <img>, NON UNO SFONDO, e non è una scelta estetica: il menu del tasto destro
// offre «apri immagine in una nuova scheda» soltanto sopra un'immagine vera. Disegnata come
// `background-image` quella voce non compare affatto, e chi lavora su un materiale perde il modo
// più diretto che ha di vedere la texture intera, alla sua risoluzione.
const Frame = styled.span<{ $src?: string }>`
    display: block;
    width: 100%;
    height: 100%;
    border-radius: 5px;
    overflow: hidden;

    /* VUOTA È UN CONTORNO, NON UN RIQUADRO PIENO: un quadratino colorato dove la texture non c'è
       si legge come una texture di quel colore. Il contorno dice «qui ne va una» e basta. */
    background-color: ${(props) => (props.$src ? panel.field : 'transparent')};

    /* IL CONTORNO È UN OUTLINE, NON UN box-shadow interno: un'ombra interna si disegna sotto il
       contenuto, quindi l'immagine la coprirebbe e il bordo — con lui quello accent dell'hover —
       sparirebbe proprio sulle righe che una texture ce l'hanno. L'outline sta sopra; l'offset
       negativo lo riporta dentro il riquadro, dove stava l'ombra. */
    outline: 1px solid ${panel.line};
    outline-offset: -1px;

    ${MapButton}:hover &,
    ${MapButton}[aria-expanded='true'] & {
        outline-color: ${panel.accent};
    }
`;

/** Decorativa: chi la nomina è il canale che la contiene, e ripeterlo la farebbe leggere due volte. */
const FrameImage = styled.img`
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
`;

// L'INGRANDIMENTO AL PASSAGGIO — la stessa immagine, in grande, per il tempo in cui ci si sta sopra.
//
// UNA CASELLA DA 32px DICE CHE UNA TEXTURE C'È, non com'è fatta: una trama di pelle e una di tessuto
// a quella misura sono due quadratini grigi. Passandoci sopra l'immagine esce dal riquadro, e da lì
// il tasto destro apre il file al suo indirizzo.
//
// VIVE IN UN PORTALE PERCHÉ DEVE USCIRE, e i bordi da scavalcare sono tre: il riquadro dello slot
// taglia (`overflow: hidden`), il pannello scorre, e la colonna finisce. Ingrandita sul posto si
// fermerebbe al primo dei tre.
const ZOOM_WIDTH = 160;
const ZOOM_MAX_HEIGHT = 200;

/** Sopra il pannello e la sua barra; sotto i dialoghi, che quando ci sono coprono tutto comunque. */
const ZOOM_LAYER = 10000;

const Zoom = styled.img`
    position: fixed;
    z-index: ${ZOOM_LAYER};
    width: ${ZOOM_WIDTH}px;
    height: auto;
    max-height: ${ZOOM_MAX_HEIGHT}px;
    border-radius: 6px;
    background: ${panel.field};
    outline: 1px solid ${panel.line};
    outline-offset: -1px;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.22);
`;

type ZoomSpot = { left: number; top?: number; bottom?: number };

export const PropertiesPanelMapThumb: React.FC<{ $src?: string }> = ({ $src }) => {
    const frame = useRef<HTMLSpanElement>(null);
    const closing = useRef<number | undefined>(undefined);
    const [zoom, setZoom] = useState<ZoomSpot | null>(null);
    const shown = zoom !== null;

    // STA SOPRA LA CASELLA E LA TOCCA, non sopra di lei: coprendola le ruberebbe il clic, e il clic
    // sulla casella è ciò che apre il cassetto del canale. Attaccata, per entrarci basta salire.
    //
    // CRESCENDO IN SU SI ANCORA COL `bottom` e non col `top`: l'altezza dipende dalle proporzioni
    // della texture e non si conosce prima di averla disegnata, mentre il bordo da cui parte si sa
    // già. Ancorarla in alto vorrebbe dire misurarla, e per misurarla bisogna prima mostrarla.
    const place = useCallback(() => {
        const rect = frame.current?.getBoundingClientRect();
        if (!rect) return;
        const left = Math.max(8, Math.min(rect.left, window.innerWidth - ZOOM_WIDTH - 8));
        setZoom(rect.top >= ZOOM_MAX_HEIGHT + 8 ? { left, bottom: window.innerHeight - rect.top } : { left, top: rect.bottom });
    }, []);

    const show = useCallback(() => {
        window.clearTimeout(closing.current);
        place();
    }, [place]);

    // IL RITARDO È IL CONFINE. Fra la casella e l'immagine ingrandita il puntatore passa comunque per
    // un istante in cui non è su nessuna delle due, e senza attesa si spegnerebbe proprio mentre ci
    // si sta andando — cioè sempre, perché è l'unico modo di arrivarci.
    const hide = useCallback(() => {
        window.clearTimeout(closing.current);
        closing.current = window.setTimeout(() => setZoom(null), 120);
    }, []);

    useEffect(() => () => window.clearTimeout(closing.current), []);

    // IL CESTINO PORTA VIA ANCHE L'INGRANDIMENTO. Svuotata la casella non ha più di che accorgersi
    // che il mouse se ne va — i suoi gestori se ne vanno con l'immagine — e senza questo resterebbe
    // aperto un ritratto di un file che non c'è più, in ascolto dello scorrimento per sempre.
    useEffect(() => {
        if (!$src) setZoom(null);
    }, [$src]);

    // Il pannello scorre sotto di lei: ferma al rettangolo di prima si staccherebbe dalla casella.
    useEffect(() => {
        if (!shown) return;

        window.addEventListener('scroll', place, true);
        window.addEventListener('resize', place);

        return () => {
            window.removeEventListener('scroll', place, true);
            window.removeEventListener('resize', place);
        };
    }, [shown, place]);

    return (
        <>
            <Frame ref={frame} $src={$src} onMouseEnter={$src ? show : undefined} onMouseLeave={$src ? hide : undefined}>
                {$src && <FrameImage src={$src} alt='' />}
            </Frame>
            {$src && zoom && createPortal(<Zoom src={$src} alt='' style={zoom} onMouseEnter={show} onMouseLeave={hide} />, document.body)}
        </>
    );
};

/** Dove una mappa non c'è la colonna resta lo stesso: a tenerla è questo. */
const MapGap = styled.span`
    width: ${MAP_SIZE};
`;

// DENTRO, LE RIGHE STANNO PIÙ STRETTE CHE FUORI: qui non ci sono anteprime a dettare l'altezza, e
// il titolo di ciò che si legge è il canale che le contiene.
const Body = styled.div`
    grid-column: 1 / -1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 8px;
    padding-top: 8px;
`;

// IL DIVISORE SEPARA DUE GENERI DI CONTENUTO — i valori che il canale governa e il file che lo
// dipinge — e per questo va da bordo a bordo: un confine che non arriva ai bordi non è un confine.
const Divider = styled.span`
    flex: none;
    height: 1px;
    margin: 6px 0;
    background: ${panel.line};
`;

const openSurface = `color-mix(in srgb, ${panel.ink} 6%, ${panel.surface})`;

const Row = styled.div<{ $open: boolean; $openable: boolean }>`
    display: grid;
    grid-template-columns: 14px minmax(0, 84px) minmax(0, 1fr) ${MAP_SIZE};
    column-gap: 6px;
    align-items: center;

    /* L'ALTEZZA DELLA TESTATA È DICHIARATA SULLA RIGA DELLA GRIGLIA e non dedotta dal contenitore:
       da chiusa la riga ha una riga di griglia, da aperta due, e un'altezza minima messa sul
       contenitore smetterebbe di stirare la prima nel momento in cui compare la seconda — cioè il
       nome salterebbe in su proprio mentre lo si apre. */
    grid-auto-rows: minmax(${MAP_SIZE}, auto);

    /* L'ARIA CE L'HANNO TUTTE LE RIGHE, aperte o chiuse: pagata sempre, non è più un movimento — è
       la forma dell'elenco. Il passo verticale fra due canali lo tiene il corpo della card, come
       per ogni altra riga; il margine negativo pareggia il solo rientro orizzontale, così il
       contenuto resta sulla x delle colonne e la superficie di ciò che si apre ha di che sbordare. */
    padding: 4px;
    margin: 0 -4px;
    border-radius: 6px;
    background: ${(props) => (props.$open ? openSurface : 'transparent')};

    /* APERTO È UNA SUPERFICIE, NON UNA CORNICE: un contorno dentro una card è un rettangolo dentro
       un rettangolo a un pixel dai suoi fratelli. Il tono si mescola all'inchiostro invece di
       essere scritto, così il gradino va nel verso giusto in tutti e due i temi — in giù sul
       chiaro, in su sullo scuro — con una dichiarazione sola. */
    &:hover {
        background: ${(props) => (props.$open ? openSurface : props.$openable ? `color-mix(in srgb, ${panel.ink} 4%, transparent)` : 'transparent')};
    }
`;

export type PropertiesPanelChannelProps = {
    id: string;
    label: React.ReactNode;
    /** Il controllo che resta in vista. Assente: sulla riga c'è la sola mappa. */
    head?: React.ReactNode;
    /** I valori che il canale governa, nel cassetto. */
    values?: React.ReactNode;
    /** Lo slot della mappa e le sue impostazioni, sotto il divisore. */
    mapSettings?: React.ReactNode;
    /** L'anteprima della texture, quando c'è. */
    preview?: string;
    /** Il canale una mappa la può avere: la colonna si riserva anche quando è vuota. */
    hasMap?: boolean;
    defaultIsOpen?: boolean;
};

export const PropertiesPanelChannel: React.FC<PropertiesPanelChannelProps> = ({ id, label, head, values, mapSettings, preview, hasMap, defaultIsOpen }) => {
    const [isOpen, setIsOpen] = useState(defaultIsOpen ?? false);
    const openable = Boolean(values || mapSettings);
    const toggle = () => setIsOpen((open) => !open);

    // Il nome di un canale è una parola, non un frammento di interfaccia: quando lo è davvero, è
    // anche ciò che nomina l'anteprima per chi non la vede.
    const name = typeof label === 'string' ? label : undefined;

    return (
        <Row $open={isOpen} $openable={openable}>
            {/* IL SEGNAPOSTO DEL LUCCHETTO: vedi `PropertiesPanelBullets`. Un pallino dichiara la colonna
                — «qui ci va qualcosa, per ora niente» — dove una colonna vuota sembrerebbe un rientro. */}
            <PropertiesPanelBullet aria-hidden='true' />

            {openable ? (
                <Name type='button' id={`${id}-name`} title={name} $full={!head} aria-expanded={isOpen} aria-controls={`${id}-body`} onClick={toggle}>
                    {label}
                </Name>
            ) : (
                <Name as='span' id={`${id}-name`} title={name} $full={!head}>
                    {label}
                </Name>
            )}

            {/* Il controllo in riga è del canale, che ha già il suo pallino: niente colonna qui. */}
            {head && (
                <Head>
                    <PropertiesPanelBulletsProvider value='off'>{head}</PropertiesPanelBulletsProvider>
                </Head>
            )}

            {hasMap ? (
                <MapButton type='button' aria-expanded={isOpen} aria-controls={`${id}-body`} aria-labelledby={`${id}-name`} onClick={toggle}>
                    <PropertiesPanelMapThumb $src={preview} />
                </MapButton>
            ) : (
                <MapGap aria-hidden='true' />
            )}

            {isOpen && (
                <Body id={`${id}-body`}>
                    <PropertiesPanelBulletsProvider value='blank'>
                        {values}
                        {values && mapSettings && <Divider aria-hidden='true' />}
                        {mapSettings}
                    </PropertiesPanelBulletsProvider>
                </Body>
            )}
        </Row>
    );
};
